import React, { useState, useEffect, useContext } from 'react';
import { adminGet } from '../adminFetch';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../AuthContext';
import Swal from 'sweetalert2';
import './AdminEditCategory.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const AdminEditCategory = () => {
  const { authFetch } = useContext(AuthContext);
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [category, setCategory] = useState({
    categoryID: 0,
    name: '',
    description: '',
    parentCategoryId: null,
  });
  
  const [categories, setCategories] = useState([]);
  const [imagePreview, setImagePreview] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [hasExistingImage, setHasExistingImage] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // adminGet so an inactive category can still be opened and re-activated
        const categoryResponse = await adminGet(`${API_BASE_URL}/api/categories/${id}`);
        if (!categoryResponse.ok) {
          throw new Error('Failed to fetch category');
        }
        const categoryData = await categoryResponse.json();
        setCategory(categoryData);
        debugger;
        if (categoryData.imagePath) { 
          setImagePreview(categoryData.imageFileName);
          setHasExistingImage(true);
        }
        
        const categoriesResponse = await adminGet(`${API_BASE_URL}/api/categories`);
        if (!categoriesResponse.ok) {
          throw new Error('Failed to fetch categories');
        }
        const categoriesData = await categoriesResponse.json();
        
        const filteredCategories = categoriesData.filter(
          (cat) => cat.categoryID !== parseInt(id)
        );
        setCategories(filteredCategories);
      } catch (err) {
        Swal.fire({
          icon: 'error',
          title: 'Loading Error',
          text: `Failed to load category: ${err.message}`,
          confirmButtonColor: '#45a049'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCategory({ 
      ...category, 
      [name]: name === 'parentCategoryId' && value === '' ? null : value 
    });
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        Swal.fire({
          icon: 'error',
          title: 'File Too Large',
          text: 'Please select an image under 5MB.',
          confirmButtonColor: '#e74c3c'
        });
        return;
      }
      if (!file.type.match('image.*')) {
        Swal.fire({
          icon: 'error',
          title: 'Invalid File Type',
          text: 'Please select a valid image file (JPEG, PNG, etc).',
          confirmButtonColor: '#e74c3c'
        });
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        const imageUrl = reader.result;
        setImagePreview(imageUrl);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleClearImage = () => {
    if (hasExistingImage && !imageFile) {
      Swal.fire({
        title: 'Remove Image?',
        text: 'A category must have an image. You will need to upload a new image before saving.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#e74c3c',
        cancelButtonColor: '#7a7a7a',
        confirmButtonText: 'Continue'
      }).then((result) => {
        if (result.isConfirmed) {
          setImageFile(null);
          setImagePreview('');
          setHasExistingImage(false);
          
          setCategory({
            ...category,
            imagePath: null,
            imageFileName: null
          });
        }
      });
    } else {
      setImageFile(null);
      setImagePreview('');
      
      if (hasExistingImage) {
        setHasExistingImage(false);
        setCategory({
          ...category,
          imagePath: null,
          imageFileName: null
        });
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!imageFile && !hasExistingImage) {
      Swal.fire({
        icon: 'warning',
        title: 'Image Required',
        text: 'Please upload an image for the category.',
        confirmButtonColor: '#45a049'
      });
      return;
    }
    
    Swal.fire({
      title: 'Updating Category',
      text: 'Please wait...',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('CategoryID', category.categoryID);
      formData.append('Name', category.name);
      formData.append('Description', category.description || '');
      formData.append('IsActive', category.isActive !== false);
      
      if (category.parentCategoryId) {
        formData.append('ParentCategoryId', category.parentCategoryId);
      }
      
      if (imageFile) {
        formData.append('ImageFile', imageFile);
      }
      
      const response = await authFetch(`${API_BASE_URL}/api/categories/${id}`, {
        method: 'PUT',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to update category');
      }

      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'Category updated successfully!',
        timer: 1500,
        confirmButtonColor: '#45a049'
      }).then(() => {
        navigate('/admin/category');
      });
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Update Failed',
        text: `Error updating category: ${err.message}`,
        confirmButtonColor: '#e74c3c'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategory = async () => {
    Swal.fire({
      title: 'Delete Category',
      text: 'Are you sure you want to delete this category?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#e74c3c',
      cancelButtonColor: '#7a7a7a',
      confirmButtonText: 'Yes',
      cancelButtonText: 'Cancel'
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true);
        try {
          Swal.fire({
            title: 'Deleting Category',
            text: 'Please wait...',
            allowOutsideClick: false,
            didOpen: () => {
              Swal.showLoading();
            }
          });
  
          const response = await authFetch(`${API_BASE_URL}/api/categories/${id}`, {
            method: 'DELETE',
          });
  
          if (!response.ok) {
            const errorData = await response.text();
            const errorMessage = errorData || 'Failed to delete category';
            throw new Error(errorMessage);
          }
  
          Swal.fire({
            icon: 'success',
            title: 'Deleted!',
            text: 'Category deleted successfully!',
            timer: 1500,
            confirmButtonColor: '#45a049'
          }).then(() => {
            navigate('/admin/category');
          });
        } catch (err) {
          
          let displayMessage = err.message;
          
          if (typeof displayMessage === 'object' && displayMessage.message) {
            displayMessage = displayMessage.message;
          }
          
          Swal.fire({
            icon: 'error',
            title: 'Deletion Failed',
            text: displayMessage,
            confirmButtonColor: '#e74c3c'
          });
        } finally {
          setLoading(false);
        }
      }
    });
  };

  if (loading) {
    return (
      <div className="admin-edit-category-loading">
        Loading category details...
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-edit-category-container">
        <div className="admin-edit-category-error">
          Error: {error}
        </div>
        <button 
          className="admin-edit-category-back-btn" 
          onClick={() => navigate('/admin/category')}
        >
          Back to Categories
        </button>
      </div>
    );
  }

  return (
    <div className="admin-edit-category-container">
      {/* Back Button */}
      <div className="admin-edit-category-header">
        <button 
          className="admin-edit-category-back-btn" 
          onClick={() => navigate('/admin/category')}
        >
          <i className="fas fa-arrow-left"></i> Back to Categories
        </button>
      </div>
      
      {/* Title Section */}
      <div className="admin-edit-category-title">
        <h1>Edit Category</h1>
      </div>
      
      {/* Category Form */}
      <form onSubmit={handleSubmit} className="admin-edit-category-details-form">
        <div className="admin-edit-category-form-group">
          <label>Category Name:</label>
          <input
            type="text"
            name="name"
            value={category.name}
            onChange={handleChange}
            required
          />
        </div>
        
        <div className="admin-edit-category-form-group">
          <label>Description:</label>
          <textarea
            name="description"
            value={category.description || ''}
            onChange={handleChange}
            required
          />
        </div>
        
        <div className="admin-edit-category-form-group" style={{ textAlign: 'left' }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap' }}>
            Active (show on website): <input
              type="checkbox"
              checked={category.isActive !== false}
              onChange={(e) => setCategory({ ...category, isActive: e.target.checked })}
            />
          </label>
          <small style={{ display: 'block', marginTop: '4px', color: '#777' }}>
            Uncheck to hide this category and all of its products from customers.
          </small>
        </div>

        <div className="admin-edit-category-form-group">
          <label>Parent Category (Optional):</label>
          <select
            name="parentCategoryId"
            value={category.parentCategoryId || ''}
            onChange={handleChange}
          >
            <option value="">None (Top-Level Category)</option>
            {categories.map((cat) => (
              <option key={cat.categoryID} value={cat.categoryID}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>
        
        <div className="admin-edit-category-form-group">
          <label>Category Image:</label>
          <div className="admin-edit-category-file-input">
            <label htmlFor="categoryImageUpload">
              {imagePreview ? (
                <div className="admin-edit-category-current-image">
                  <p style={{ marginBottom: '10px', color: '#666' }}>
                    {hasExistingImage && !imageFile ? 'Current image:' : 'New image to upload:'}
                  </p>
                  <img 
                    src={imagePreview} 
                    alt="Category Preview" 
                    style={{ 
                      maxWidth: '300px', 
                      maxHeight: '300px', 
                      objectFit: 'contain',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      padding: '5px',
                      backgroundColor: '#f8f9fa'
                    }} 
                  />
                </div>
              ) : (
                <div className="admin-edit-category-upload-placeholder" style={{
                  border: '2px dashed #ccc',
                  borderRadius: '8px',
                  height: '200px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  backgroundColor: '#f8f9fa'
                }}>
                  <i className="fas fa-cloud-upload-alt" style={{ fontSize: '40px', color: '#ccc', marginBottom: '10px' }}></i>
                  <span style={{ color: '#666', fontSize: '16px', marginBottom: '5px' }}>Click to select an image</span>
                  <span style={{ color: '#999', fontSize: '13px' }}>Recommended size: 800×600px, max 5MB</span>
                </div>
              )}
              <input
                id="categoryImageUpload"
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                style={{ display: 'none' }}
              />
            </label>
          </div>
          {imagePreview && (
            <div style={{ marginTop: '10px' }}>
              <button
                type="button"
                onClick={handleClearImage}
                style={{ 
                  padding: '8px 12px', 
                  fontSize: '13px',
                  backgroundColor: '#ffebee',
                  color: '#c62828',
                  border: '1px solid #ffcdd2',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '5px'
                }}
              >
                <i className="fas fa-times"></i> Remove Image
              </button>
            </div>
          )}
        </div>
        
        <div className="admin-edit-category-buttons-container">
          <button
            type="submit"
            className="admin-edit-category-btn-primary"
            disabled={loading}
          >
            <i className="fas fa-save"></i> {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
      
      {/* Action Buttons */}
      <div className="admin-edit-category-actions">
        <button
          type="button"
          className="admin-edit-category-btn-delete"
          onClick={handleDeleteCategory}
        >
          <i className="fas fa-trash-alt"></i> Delete Category
        </button>
      </div>
    </div>
  );
};

export default AdminEditCategory;